/**
 * Consolidated portfolio figures (HP-C, Pedro 2026-10-19). Pure (no React), checked on its own.
 *
 * A row (one security across portfolios) carries each portfolio's figures as stored. A
 * SCOPE is one portfolio, or "total" = every portfolio of the view that has positions in it
 * (the Portfolios picker). Rules agreed with Pedro (Q4):
 *   - quantity, value (security ccy), value (reference ccy), total cost, total P&L: sums;
 *   - Price: one portfolio → its own price; several → the lines' own prices weighted by
 *     quantity, Σ (price × quantity) ÷ Σ quantity (ACC-1, 2026-10-23; was Σ value ÷ Σ quantity,
 *     wrong for a bond: its value includes accrued interest and its price is per 100 nominal);
 *   - Value (reference ccy) includes accrued interest (ACC-1: the server adds the file's Accrued
 *     interest, already in the reference ccy, to its clean Net value); Clean value and Accrued
 *     interest (reference ccy) are its two parts;
 *   - Avg purchase price (security ccy): the file's own Average purchase price; several lines →
 *     weighted by quantity (C-411, 2026-10-27; was Σ cost ÷ Σ quantity, wrong: see below);
 *   - Total cost: the file gives it in the line's REFERENCE currency (Quantity × Average purchase
 *     price × Average buy exchange rate — C-411, checked on real BNP lines), crossed to the shown
 *     currency like Value; a cost the custodian does not give (0 with APP 0) is shown as given, 0,
 *     and the page warns (Pedro 2026-10-27);
 *   - FX: the custodian's exchange RATE, security ccy → shown ccy (FX-1, 2026-10-22; was
 *     Σ value ÷ Σ value): 1 when they are the same; the file's own rate when every line is in
 *     its own reference currency = the shown one; else the date's rates, crossed via EUR;
 *   - % of portfolio: of that portfolio's total; for "total", of the consolidated total.
 * Currencies (Q3, option a): every amount the file gives in its reference currency (Net value,
 * Total P&L) arrives here in EUR — since 3a (2026-10-21) the server converts a non-EUR
 * reference currency to EUR at the files' own rates, null when there is none — and is
 * converted to the chosen reference currency at the custodian's rate for the view's date;
 * so is the Total cost (C-411; it was wrongly read as in the purchase currency until 2026-10-27). A figure that cannot be
 * converted (no rate for a currency, no purchase currency stored) is null → shown "-".
 * A security a portfolio does not hold is null for that portfolio → shown "-" (Q5).
 */
import type { ConsolidatedLine, ConsolidatedRow } from '../api/portfolios';

export type Scope = string;           // a portfolio number, or TOTAL
export const TOTAL = 'total';

export type Measure =
  | 'quantity' | 'price' | 'value_pos' | 'fx' | 'value_ref' | 'clean_ref' | 'accrued_ref' | 'pct' | 'pct_cons' | 'accrued'
  | 'avg_price' | 'total_cost' | 'total_pnl';

export interface Ctx {
  /** The portfolios forming the view's Total (those with positions in the view). */
  inView: string[];
  /** Each in-view portfolio's total value, EUR. */
  portfolioTotalEur: Record<string, number>;
  /** EUR per 1 unit of a currency (the custodians' pooled, the view's date): the fallback. */
  rates: Record<string, number>;
  /** TOT-DASH (Pedro 2026-10-22): each custodian's own rates, by bank id (the part of a ref
   *  before ':'); a portfolio is converted with its custodian's. */
  ratesByBank?: Record<string, Record<string, number>>;
  /** RATE-SHADE: per bank, the currencies whose rate is a substitute (typed, the custodian's
   *  last, another custodian's): a figure converted with one is shaded on the page. */
  substituted?: Record<string, string[]>;
  /** 3a: each in-view portfolio's reference currency and gross value in it (no rate needed
   *  to show a portfolio in its own currency). Optional: absent → everything goes through EUR. */
  portfolioRef?: Record<string, { ccy: string; gross: number | null }>;
  /** The reference currency the amounts are shown in. */
  ref: string;
}

const n = (s: string | null | undefined): number | null => (s == null || s === '' ? null : Number(s));

/** Convert an amount from one currency to another through EUR; null when a rate is missing. */
export function convert(amount: number, from: string, to: string, rates: Record<string, number>): number | null {
  if (from === to) return amount;
  const rf = rates[from], rt = rates[to];
  return rf && rt ? (amount * rf) / rt : null;
}

/** The rates a portfolio (ref "bank:number") is converted with: its custodian's, else pooled. */
export function ratesFor(p: string, ctx: Ctx): Record<string, number> {
  const i = p.indexOf(':');
  return (i > 0 ? ctx.ratesByBank?.[p.slice(0, i)] : undefined) ?? ctx.rates;
}

/** The scope's lines, each with its portfolio's rates. */
type Entry = { l: ConsolidatedLine; r: Record<string, number> };
function entriesOf(row: ConsolidatedRow, scope: Scope, ctx: Ctx): Entry[] {
  const nums = scope === TOTAL ? ctx.inView : [scope];
  return nums.filter((p) => !!row.by_portfolio[p]).map((p) => ({ l: row.by_portfolio[p], r: ratesFor(p, ctx) }));
}

/** C-411 (BNP, checked on real lines 2026-10-27): the custodian gives no cost for this line when
 *  Total cost is blank, or 0 with an Average purchase price of 0 or blank (e.g. a bond transferred
 *  in): its cost and P&L are not reliable. Pedro 2026-10-27: the figures are still SHOWN as the
 *  custodian gives them (0), with a warning banner naming such lines (View portfolio). */
export function costUnknown(l: { total_cost?: string | null; avg_price?: string | null }): boolean {
  const c = n(l.total_cost ?? null);
  if (c == null) return true;
  const a = n(l.avg_price ?? null);
  return c === 0 && (a == null || a === 0);
}

/** A line's total cost in `to`; null when unknown or not convertible. C-411: the file's Total cost
 *  is in the line's REFERENCE currency (Quantity × Average purchase price × Average buy exchange
 *  rate), never in the purchase currency; crossed to `to` like the other reference-currency parts. */
function costIn({ l, r }: Entry, to: string): number | null {
  const c = n(l.total_cost);
  return c == null ? null : convert(c, l.ref_currency ?? 'EUR', to, r);
}

/** A line's Net value (or Total P&L) in the shown currency: as the file gives it when that is
 *  the line's own reference currency (no rate needed, 3a); otherwise from EUR. */
function refFigure({ l, r }: Entry, which: 'value' | 'pnl', ctx: Ctx): number | null {
  const own = which === 'value' ? l.value_ref : l.total_pnl_ref;
  if ((l.ref_currency ?? 'EUR') === ctx.ref && own != null) return n(own);
  const eur = n(which === 'value' ? l.value_eur : l.total_pnl);
  return eur == null ? null : convert(eur, 'EUR', ctx.ref, r);
}

/** ACC-1: a reference-currency part of the value (clean value, accrued interest) in the shown
 *  currency: as given when the line's reference currency is the shown one, else crossed via EUR
 *  at the portfolio's custodian's rates (the same rates the value itself uses). */
function refPart({ l, r }: Entry, field: 'clean_ref' | 'accrued_ref', ctx: Ctx): number | null {
  const v = n(l[field]);
  if (v == null) return null;
  return convert(v, l.ref_currency ?? 'EUR', ctx.ref, r);
}

/** A portfolio's gross value in the shown currency; null when it cannot be had. */
export function grossIn(p: string, ctx: Ctx): number | null {
  const own = ctx.portfolioRef?.[p];
  if (own && own.ccy === ctx.ref && own.gross != null) return own.gross;
  const eur = ctx.portfolioTotalEur[p];
  return eur == null ? null : convert(eur, 'EUR', ctx.ref, ratesFor(p, ctx));
}

/** Σ of a per-line figure; null if no line, or if any line's figure is null. */
function sumOf<T>(ls: T[], f: (l: T) => number | null): number | null {
  if (ls.length === 0) return null;
  let s = 0;
  for (const l of ls) { const v = f(l); if (v == null) return null; s += v; }
  return s;
}

/** One figure of a row for a scope, in the units of the column (see the module notes). */
export function measure(row: ConsolidatedRow, scope: Scope, m: Measure, ctx: Ctx): number | null {
  const es = entriesOf(row, scope, ctx);
  const ls = es.map((e) => e.l);
  if (ls.length === 0) return null;
  switch (m) {
    case 'quantity': return sumOf(ls, (l) => n(l.quantity));
    case 'value_pos': return sumOf(ls, (l) => n(l.value_pos));
    case 'accrued': return sumOf(ls, (l) => n(l.accrued));
    case 'value_ref': return sumOf(es, (e) => refFigure(e, 'value', ctx));
    case 'clean_ref': return sumOf(es, (e) => refPart(e, 'clean_ref', ctx));
    case 'accrued_ref': return sumOf(es, (e) => refPart(e, 'accrued_ref', ctx));
    case 'total_pnl': return sumOf(es, (e) => refFigure(e, 'pnl', ctx));
    case 'total_cost': return sumOf(es, (e) => costIn(e, ctx.ref));
    case 'price': {
      if (ls.length === 1 && (scope !== TOTAL || ctx.inView.length === 1)) return n(ls[0].price);
      // ACC-1: the lines' own prices weighted by quantity (never value ÷ quantity, see above).
      const q = sumOf(ls, (l) => n(l.quantity));
      const pq = sumOf(ls, (l) => { const p = n(l.price), qq = n(l.quantity); return p == null || qq == null ? null : p * qq; });
      return q && pq != null ? pq / q : null;
    }
    case 'avg_price': {
      // C-411: the file's Average purchase price (security currency); several lines → weighted by
      // quantity (one row = one ISIN + currency, so they share the currency). A line with no cost
      // from the custodian counts with its 0, as given (Pedro 2026-10-27; the page warns).
      if (ls.length === 1) return n(ls[0].avg_price);
      const q = sumOf(ls, (l) => n(l.quantity));
      const pq = sumOf(ls, (l) => { const a = n(l.avg_price), qq = n(l.quantity); return a == null || qq == null ? null : a * qq; });
      return q && pq != null ? pq / q : null;
    }
    case 'fx': {
      // FX-1 (Pedro 2026-10-22): a RATE, never a ratio of values (Net value and Valuation are
      // cent-rounded, so their ratio drifts line to line from about the 7th decimal).
      if (row.currency === ctx.ref) return 1;
      // Every line in its own reference currency = the shown one: the custodian's rate as the
      // file gives it (value-weighted if several portfolios give slightly different rates).
      if (ls.every((l) => (l.ref_currency ?? 'EUR') === ctx.ref && n(l.fx) != null)) {
        const w = ls.reduce((s, l) => s + Math.abs(n(l.value_pos) ?? 0), 0);
        if (w > 0) return ls.reduce((s, l) => s + (n(l.fx) as number) * Math.abs(n(l.value_pos) ?? 0), 0) / w;
        return n(ls[0].fx);
      }
      // Otherwise: each line's custodian's rates of the view's date, crossed via EUR; lines of
      // several custodians are value-weighted (TOT-DASH); one without a rate makes it "-".
      const crossed = es.map((e) => ({ r: convert(1, row.currency, ctx.ref, e.r), w: Math.abs(n(e.l.value_pos) ?? 0) }));
      if (crossed.some((x) => x.r == null)) return null;
      const w = crossed.reduce((s, x) => s + x.w, 0);
      return w > 0 ? crossed.reduce((s, x) => s + (x.r as number) * x.w, 0) / w : (crossed[0].r as number);
    }
    case 'pct': {
      const scopes = scope === TOTAL ? ctx.inView : [scope];
      // One portfolio: the server's own % (of its gross, in its own currency: never needs a rate).
      if (scopes.length === 1 && ls.length === 1 && ls[0].pct != null) return n(ls[0].pct);
      // Several: a share of the consolidated gross in the shown currency. A portfolio whose gross
      // cannot be had in it (no rate, 3a) makes the % unknown, not smaller.
      const v = sumOf(es, (e) => refFigure(e, 'value', ctx));
      const totals = scopes.map((p) => grossIn(p, ctx));
      if (v == null || totals.some((t) => t == null)) return null;
      const total = (totals as number[]).reduce((s, t) => s + t, 0);
      return total === 0 ? null : (v / total) * 100;
    }
    case 'pct_cons': {
      // TRADE-CONS 4b (Pedro 2026-10-30: "an additional column group which is "% of consolidation",
      // but per portfolio … The %s add up to 100% over all rows of all portfolios"): the scope's
      // value as a share of the combined gross of EVERY portfolio of the view (its Total = 'pct' Total).
      const v = sumOf(es, (e) => refFigure(e, 'value', ctx));
      const totals = ctx.inView.map((p) => grossIn(p, ctx));
      if (v == null || totals.some((t) => t == null)) return null;
      const total = (totals as number[]).reduce((s, t) => s + t, 0);
      return total === 0 ? null : (v / total) * 100;
    }
  }
}

/** RATE-SHADE (Pedro 2026-10-22: "shade the cells where the manually input FX rate is being used";
 *  "shade all FX rates that depend on the manually input one"): does this figure use a
 *  substituted rate? Follows the same paths as `measure`: a figure shown in the line's own
 *  currency, or the file's own FX, needs no rate. */
export function usesSubstitute(row: ConsolidatedRow, scope: Scope, m: Measure, ctx: Ctx): boolean {
  if (!ctx.substituted) return false;
  const nums = scope === TOTAL ? ctx.inView : [scope];
  const subOf = (p: string) => {
    const i = p.indexOf(':');
    return new Set(i > 0 ? ctx.substituted?.[p.slice(0, i)] ?? [] : []);
  };
  const held = nums.filter((p) => !!row.by_portfolio[p]);
  if (m === 'fx') {
    if (row.currency === ctx.ref) return false;
    if (held.every((p) => (row.by_portfolio[p].ref_currency ?? 'EUR') === ctx.ref && n(row.by_portfolio[p].fx) != null)) return false;
    return held.some((p) => { const s = subOf(p); return s.has(row.currency) || s.has(ctx.ref); });
  }
  // C-411: Total cost is in the reference currency, so it is shaded exactly as Value is; Avg
  // purchase price is the file's, in the security currency, and never needs a rate.
  if (m === 'value_ref' || m === 'total_pnl' || m === 'clean_ref' || m === 'accrued_ref' || m === 'total_cost') {
    return held.some((p) => {
      const l = row.by_portfolio[p];
      const own = l.ref_currency ?? 'EUR';
      if (own === ctx.ref) return false;
      const s = subOf(p);
      return s.has(own) || s.has(ctx.ref);
    });
  }
  return false;
}

/** Measures whose group / total rows are sums of the rows (the others are not summed). */
export const SUMMABLE: Measure[] = ['value_ref', 'clean_ref', 'accrued_ref', 'total_cost', 'total_pnl'];
